const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

export const TrainerAPI = {
  getDashboardStats: async (email) => {
    const res = await fetch(`${API_BASE_URL}/trainer/dashboard`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error("Failed to load dashboard stats");
    return res.json();
  },

  getBatches: async (email) => {
    const res = await fetch(`${API_BASE_URL}/trainer/batches`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error("Failed to load batches");
    const data = await res.json();
    return data.data;
  },

  // Batches this trainer used to be assigned to but has since been
  // reassigned off of — separate endpoint (see trainer.controller.js) since
  // finding them requires a full scan, unlike the Query-backed getBatches
  // above, so it only runs when the "Previous Batches" tab is opened.
  getPreviousBatches: async () => {
    const res = await fetch(`${API_BASE_URL}/trainer/batches/previous`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error("Failed to load previous batches");
    const data = await res.json();
    return data.data;
  },

  // Invoice status is optional/best-effort — the Invoice Service is a separate
  // dependency and can be slow or unreachable independently of the app itself.
  // A client-side timeout ensures a hung request doesn't block the caller
  // forever, on top of the backend's own timeout around its upstream call.
  getTrainerInvoices: async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    try {
      const res = await fetch(`${API_BASE_URL}/trainer/invoices`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        signal: controller.signal,
      });

      if (!res.ok) throw new Error('Failed to load invoices');
      const data = await res.json();
      return data.data || [];
    } finally {
      clearTimeout(timeoutId);
    }
  },

  raiseTrainerInvoice: async (payload) => {
    const res = await fetch(`${API_BASE_URL}/trainer/invoices/raise`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to submit invoice');
    return data;
  },

  getBatchDetails: async (batchNo, email) => {
    const res = await fetch(`${API_BASE_URL}/trainer/batch/${batchNo}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error("Failed to load batch details");
    return res.json();
  },

  // Latest TC_Batch_Journey snapshot for a batch — the source of truth for
  // per-student status/attendance, since it's saved fresh every time a
  // session is marked (unlike TC_Batches.students, which only reflects the
  // last write back to the batch record).
  getBatchJourneyStatus: async (batchNo) => {
    const res = await fetch(`${API_BASE_URL}/trainer/batch/${batchNo}/journey/latest`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error("Failed to load batch journey status");
    return res.json();
  },

  getTrainerProfile: async () => {
    const res = await fetch(`${API_BASE_URL}/trainer/profile`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error("Failed to fetch trainer profile");
    return res.json();
  },

  updateTrainerProfile: async (data) => {
    const res = await fetch(`${API_BASE_URL}/trainer/profile`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) throw new Error('Failed to update trainer profile');
    return res.json();
  },

  // 🆕 NEW: Upload profile image
  uploadProfileImage: async (file) => {
    const formData = new FormData();
    formData.append('photo', file);

    const res = await fetch(`${API_BASE_URL}/trainer/upload-photo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: formData,
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || 'Failed to upload profile image');
    }
    
    const data = await res.json();
    return {
      imageUrl: data.profile_image_url,
      message: data.message
    };
  },
  // Password reset API
  updatePassword: async (passwordData) => {
    const res = await fetch(`${API_BASE_URL}/auth/update-password`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(passwordData),
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || 'Failed to update password');
    }
    return res.json();
  },

  //  Onboarding - Get status
  getOnboardingStatus: async () => {
    const res = await fetch(`${API_BASE_URL}/onboarding/status`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error('Failed to fetch onboarding status');
    return res.json();
  },
//  Onboarding - Get video URL
getOnboardingVideoUrl: async () => {
  const token = localStorage.getItem("authToken");

  const res = await fetch(`${API_BASE_URL}/onboarding/video-url`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await res.json();

  if (!data.success) {
    throw new Error("Failed to fetch video URL");
  }

  return data.url;
},
  
  //  Onboarding - Submit quiz
  submitQuiz: async (answers) => {
    const res = await fetch(`${API_BASE_URL}/onboarding/submit-quiz`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ answers }),
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || 'Failed to submit quiz');
    }
    return res.json();
  },

  // ─── Google Meet Session APIs ───

  /**
   * Join a batch session — creates session in DB and returns Meet link.
   * @param {object} payload - { batchNo, meetingLink, courseName, admin, trainerName, studentDetails }
   */
  joinBatchSession: async (payload) => {
    const res = await fetch(`${API_BASE_URL}/session/join`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Failed to join session');
    return data;
  },

  /**
   * Get session details by sessionId and batchNo.
   */
  getSessionDetails: async (sessionId, batchNo) => {
    const res = await fetch(`${API_BASE_URL}/session/${sessionId}/${batchNo}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error('Failed to fetch session details');
    const data = await res.json();
    return data.data;
  },

  /**
   * Get all sessions for a batch.
   */
  getBatchSessions: async (batchNo) => {
    const res = await fetch(`${API_BASE_URL}/session/batch/${batchNo}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error('Failed to fetch batch sessions');
    const data = await res.json();
    return data.data;
  },

  /**
   * Get all active sessions (pending/live).
   */
  getActiveSessions: async () => {
    const res = await fetch(`${API_BASE_URL}/session/active`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    if (!res.ok) throw new Error('Failed to fetch active sessions');
    const data = await res.json();
    return data.data;
  },

  /**
   * Manually trigger a Meet sync for a session.
   */
  triggerSessionSync: async (sessionId, batchNo) => {
    const res = await fetch(`${API_BASE_URL}/session/${sessionId}/${batchNo}/sync`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Sync failed');
    return data;
  },
};